import React, { useContext, useEffect, useRef, useState } from 'react';
import * as R from 'ramda';
import { CancelToken } from 'axios';
import { arrayOf, bool, func, number, shape } from 'prop-types';

// Components
import { Box, Button, useMediaQuery } from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import { useTheme } from '@mui/material/styles';
import Header from './Header.connector';
import UserTable from './tables/UserTable.connector';
import SkeletonTable from './tables/SkeletonTable';
import Sidebar from './sidebar/Sidebar.connector';
import MobileToolbar from './mobile-toolbar/MobileToolbar.connector';
import { AuthContext } from '../../authentication/AuthContext';
import UserEditDrawer from './user-edit-drawer/UserEditDrawer';

// Styles
import { containerStyles, securityTablesStyles } from './Security.styles';

const makeBackTitleStyles = theme => ({
  ...theme.typography.button,
  marginBottom: theme.spacing(2),
  padding: 0,
});

const Security = ({
  affiliates,
  customerId,
  canShowSidebar,
  fetchParentCompanies,
  fetchPermissionsForUser,
  getGlobalCustomerMembers,
  mobileSidebarOpen,
  permissionsPending,
  resetSecurity,
  setMobileSidebarOpen,
  updateUserPermission,
  targetUserPermissionsPending,
  targetUser,
  setParentCompaniesIndex,
  userAffiliateTable,
}) => {
  const theme = useTheme();
  const { userProfile } = useContext(AuthContext);
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });

  // should we be showing the sidebar?
  // open if canShowSidebar is true and we are either viewing desktop or the mobile control is true
  const sidebarOpen = canShowSidebar && (lgUp || mobileSidebarOpen);

  const permissionsCancelToken = useRef();
  useEffect(() => {
    fetchParentCompanies();
    return () => resetSecurity();
  }, [fetchParentCompanies, resetSecurity]);

  useEffect(() => {
    const userId = userProfile?.samaccountname;
    if (customerId && userId) {
      if (permissionsCancelToken.current?.cancel) {
        permissionsCancelToken.current.cancel();
      }
      const cancelToken = CancelToken.source();
      permissionsCancelToken.current = cancelToken;
      getGlobalCustomerMembers({ customerId });
    }
  }, [customerId, userProfile, setParentCompaniesIndex, getGlobalCustomerMembers]);

  useEffect(() => {
    if (R.isNil(selectedUser)) return;

    if (customerId && fetchPermissionsForUser) {
      if (permissionsCancelToken.current?.cancel) {
        permissionsCancelToken.current.cancel();
      }
      const cancelToken = CancelToken.source();
      permissionsCancelToken.current = cancelToken;
      fetchPermissionsForUser({ customerId, userId: selectedUser }, cancelToken.token);
      setUserDrawerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, userProfile]);

  return (
    <>
      <Box sx={{ ...containerStyles(theme) }}>
        <Sidebar open={sidebarOpen} />
        {!lgUp && <MobileToolbar />}
        <Box sx={{ ...securityTablesStyles({ sidebarOpen, theme }) }}>
          {lgUp && <Header />}
          {!lgUp && canShowSidebar && (
            <Button
              onClick={() => setMobileSidebarOpen(true)}
              sx={makeBackTitleStyles}
              variant="text"
            >
              <ChevronLeftRoundedIcon />
              Company List
            </Button>
          )}
          {permissionsPending ? (
            <SkeletonTable />
          ) : (
            <>
              <UserTable
                affiliates={affiliates || []}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                userAffiliateTable={userAffiliateTable}
              />
              <UserEditDrawer
                affiliates={affiliates}
                customerId={customerId}
                drawerOpen={userDrawerOpen}
                setDrawerOpen={setUserDrawerOpen}
                setSelectedUser={setSelectedUser}
                setTargetUser={setSelectedUser}
                targetUserPermissionsPending={targetUserPermissionsPending}
                updateUserPermission={updateUserPermission}
                user={targetUser}
                userAffiliateTable={userAffiliateTable}
              />
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

Security.propTypes = {
  affiliates: arrayOf(shape({})),
  canShowSidebar: bool.isRequired,
  customerId: number,
  fetchParentCompanies: func.isRequired,
  fetchPermissionsForUser: func.isRequired,
  getGlobalCustomerMembers: func.isRequired,
  mobileSidebarOpen: bool.isRequired,
  permissionsPending: bool.isRequired,
  resetSecurity: func.isRequired,
  setMobileSidebarOpen: func.isRequired,
  setParentCompaniesIndex: func.isRequired,
  targetUser: shape({}),
  targetUserPermissionsPending: bool.isRequired,
  updateUserPermission: func.isRequired,
  userAffiliateTable: arrayOf(shape({})),
};

Security.defaultProps = {
  affiliates: null,
  customerId: null,
  targetUser: null,
  userAffiliateTable: null,
};

export default Security;
